// src/lib/i18n/date/formatters.ts
import { format as formatGregorian } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import { format as formatJalali } from "date-fns-jalali";
import { faIR } from "date-fns-jalali/locale";
import type { Locale } from "@/lib/i18n";
import { shapeDigits } from "./digits";
import type { PresetName } from "./presets";
import { type DateInput, toDate } from "./to-date";

export interface FormatOptions {
  locale: Locale;
  preset: PresetName;
}

/** 12h AM/PM markers per locale (hardcoded for exact control). */
const MARKERS: Record<Locale, readonly [am: string, pm: string]> = {
  fa: ["ق.ظ", "ب.ظ"],
  en: ["AM", "PM"],
  ar: ["ص", "م"],
};

/**
 * Word joining the date and the time in `longDateTime`, per locale. Same
 * hardcoded-for-exact-control approach as MARKERS above -- date-fns has no
 * pattern token for this, and putting the Persian word in every locale would
 * render "17 June 2026 ساعت 14:30" on the English build.
 */
const AT_WORD: Record<Locale, string> = {
  fa: "ساعت",
  en: "at",
  ar: "الساعة",
};

/** Format `date` with `pattern` using the locale's calendar + month/weekday names. */
function rawFormat(date: Date, pattern: string, locale: Locale): string {
  if (locale === "fa") return formatJalali(date, pattern, { locale: faIR });
  return formatGregorian(date, pattern, {
    locale: locale === "ar" ? ar : enUS,
  });
}

function formatDateTimeMarker(date: Date, locale: Locale): string {
  const datePart = rawFormat(date, "yyyy/MM/dd", locale);
  const hours = date.getHours();
  const [am, pm] = MARKERS[locale];
  const marker = hours < 12 ? am : pm;
  const hour12 = String(hours % 12 || 12).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return shapeDigits(`${datePart} • ${hour12}:${minutes} ${marker}`, locale);
}

/** Locale-aware, calendar-correct date formatter. No React — safe on server. */
export function formatDate(
  input: DateInput,
  { locale, preset }: FormatOptions,
): string {
  const date = toDate(input);
  switch (preset) {
    case "numeric":
      return shapeDigits(rawFormat(date, "yyyy/MM/dd", locale), locale);
    case "long":
      return shapeDigits(rawFormat(date, "d MMMM yyyy", locale), locale);
    case "monthDay":
      return shapeDigits(rawFormat(date, "d MMMM", locale), locale);
    case "weekdayLong":
      return shapeDigits(rawFormat(date, "EEEE d MMMM yyyy", locale), locale);
    case "dateTime":
      return shapeDigits(rawFormat(date, "yyyy/MM/dd HH:mm", locale), locale);
    case "dateTimeMarker":
      return formatDateTimeMarker(date, locale);
    case "longDateTime": {
      const datePart = rawFormat(date, "d MMMM yyyy", locale);
      const timePart = rawFormat(date, "HH:mm", locale);
      return shapeDigits(`${datePart} ${AT_WORD[locale]} ${timePart}`, locale);
    }
  }
}
